import { useEffect, useRef, useState } from 'react';
import { signOut } from 'kits/authKit';
import { LAST_ACTIVITY_KEY, LOBBY_INACTIVITY_TIMEOUT_MS, LOBBY_INACTIVITY_WARNING_SECONDS } from 'shared-state/identity/constants/activityTimerConstants';

const WARNING_THRESHOLD_MS = LOBBY_INACTIVITY_TIMEOUT_MS - LOBBY_INACTIVITY_WARNING_SECONDS * 1000;

export interface ILobbyInactivityTimer {
    isWarningVisible: boolean;
    countdownSeconds: number;
    onStayLoggedIn: () => void;
}

export const useLobbyInactivityTimer = (): ILobbyInactivityTimer => {
    const [isWarningVisible, setIsWarningVisible] = useState(false);
    const [countdownSeconds, setCountdownSeconds] = useState(LOBBY_INACTIVITY_WARNING_SECONDS);

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isWarningRef = useRef(false);

    const clearCountdown = () => {
        if (countdownRef.current !== null) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
        }
    };

    const startCountdown = () => {
        clearCountdown();
        setCountdownSeconds(LOBBY_INACTIVITY_WARNING_SECONDS);
        let remaining = LOBBY_INACTIVITY_WARNING_SECONDS;

        countdownRef.current = setInterval(() => {
            remaining -= 1;
            setCountdownSeconds(remaining);
            if (remaining <= 0) {
                clearCountdown();
                signOut();
            }
        }, 1000);
    };

    const startPoll = () => {
        if (pollRef.current !== null) clearInterval(pollRef.current);

        pollRef.current = setInterval(() => {
            if (isWarningRef.current) return;
            const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
            const lastActivity = raw ? parseInt(raw, 10) : Date.now();
            const elapsed = Date.now() - lastActivity;

            if (elapsed >= WARNING_THRESHOLD_MS) {
                isWarningRef.current = true;
                setIsWarningVisible(true);
                startCountdown();
            }
        }, 60 * 1000);
    };

    const onStayLoggedIn = () => {
        clearCountdown();
        isWarningRef.current = false;
        setIsWarningVisible(false);
        setCountdownSeconds(LOBBY_INACTIVITY_WARNING_SECONDS);
        localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
        startPoll();
    };

    useEffect(() => {
        startPoll();
        return () => {
            if (pollRef.current !== null) clearInterval(pollRef.current);
            clearCountdown();
        };
    }, []);

    return { isWarningVisible, countdownSeconds, onStayLoggedIn };
};
